import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { isZagruProblem, type ZagruProblem } from '../api/problem';

export interface ProblemContext {
  kind: 'query' | 'mutation';
  /**
   * The screen renders this failure itself: a mutation whose `meta.handlesErrors` is true, or any query (queries
   * are always rendered where their data would be).
   */
  handledInline: boolean;
}

export interface CreateQueryClientOptions {
  /** Global error hook, invoked for every failed query/mutation that produced a ZagruProblem. */
  onProblem?: (p: ZagruProblem, context: ProblemContext) => void;
  staleTime?: number;
}

/** True for transport failures and 5xx; false for 4xx and client-side response validation. */
export function isRetryableProblem(error: unknown): boolean {
  if (!isZagruProblem(error)) return true;
  if (error.code === 'client.aborted' || error.code === 'client.invalid_response') return false;
  if (error.status === 0) return true;
  return error.status >= 500;
}

/**
 * QueryClient with Zagru defaults: 30s staleTime, no refetch on window focus, 2 retries only for
 * network/5xx problems, no retry for mutations, and a global `onProblem` hook.
 */
export function createQueryClient(options: CreateQueryClientOptions = {}): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (isZagruProblem(error)) options.onProblem?.(error, { kind: 'query', handledInline: true });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        if (!isZagruProblem(error)) return;
        const handledInline = mutation.meta?.handlesErrors === true || mutation.options.onError !== undefined;
        options.onProblem?.(error, { kind: 'mutation', handledInline });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: options.staleTime ?? 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => isRetryableProblem(error) && failureCount < 2,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
      },
      mutations: { retry: false },
    },
  });
}

/** Query key factories: `[context, resource, 'list', params]`, `[context, resource, 'detail', id]`. */
export const queryKeys = {
  list: (context: string, resource: string, params?: unknown): readonly unknown[] => [context, resource, 'list', params ?? {}] as const,
  detail: (context: string, resource: string, id: string): readonly unknown[] => [context, resource, 'detail', id] as const,
  custom: (...parts: unknown[]): readonly unknown[] => parts,
};
